import Button from '@mui/material/Button';
import React, { useMemo } from 'react';

import ClientProjectSelect from 'components/ClientProjectSelect';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import Dump from 'components/Dump';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import RefundLine from 'components/Transactions/RefundLine';
import ReceiptSection from 'components/Transactions/ReceiptSection';
import TxnHeaderSection from 'components/Transactions/TxnHeaderSection';
import ZeroStateView from 'components/ZeroStateView';

import { useStyles } from '../styles';
import AmountAccountRow from './AmountAccountRow';
import BottomSection from './BottomSection';
import CardPaymentStatementNote from './CardPaymentStatementNote';
import CategorySection from './CategorySection';
import DialogActionsFooter from './DialogActionsFooter';
import type { TransactionEditViewModel } from './TransactionEditViewModel';
import PayeeDateRow from './PayeeDateRow';
import RecurringOptions from './RecurringOptions';
import StaleTransactionRefreshBanner from './StaleTransactionRefreshBanner';
import TransactionEditDrawerBody from './TransactionEditDrawerBody';

type Props = {
  rootModalDrawer?: RootModalDrawerOptions;
  showRecurringOptionsInline: boolean;
  viewModel: TransactionEditViewModel;
};

export default function TransactionEditFormContent({
  rootModalDrawer,
  showRecurringOptionsInline,
  viewModel,
}: Props): React.JSX.Element {
  const {
    actions: {
      closeAll,
      createRecurring,
      disabledPayment,
      doOneTimeBill,
      goToOtherSide,
      markRecurring,
      onCreateRule,
      onDelete,
      onRefreshClick,
      onSave,
      onSetEditSeries,
      onViewRecurring,
      openReceiptSplits,
      openSplitDialog,
      openSplitDialogFromExclusionsBanner,
      resetCustomersAndProjects,
      selectAccount,
      startRecurringSelect,
      unlinkRecurring,
      unMarkAsBill,
      unMatchTransaction,
    },
    derived: {
      allAccounts,
      businessEnabled,
      coa,
      completeEnough,
      currentSplitPayments,
      currentTxn,
      date,
      dateError,
      dateInfoText,
      deleteQTip,
      disableReportsValue,
      disableSalesReceiptFields,
      editSplitsFromExcludeBannerDisabled,
      enhancedDetailView,
      focusSplits,
      fullDisableQBP,
      futureTxnsFf,
      hasSplitItemsDefined,
      hasSplitSeries,
      hasSplitTransferCounterpart,
      hideClientsAndProjects,
      hideCreateRule,
      isAccountClosed,
      isAssetPayment,
      isDirty,
      isDirtyOutside,
      isIncome,
      isManual,
      isMatched,
      isTransfer,
      isTxnFromArAccount,
      linkedExpense,
      model,
      openAccounts,
      ornamentType,
      paymentId,
      receiptId,
      refundTracker,
      showCheckNumberField,
      showMatchOrTransferRow,
      showPaymentDialogLink,
      split,
      splitSeriesFf,
      splitsDisabled,
      supportedTransfer,
      txn,
      validPayee,
      visibilityExcludeReportsDisplay,
      visibilityF2SDisplay,
    },
    setters: {
      setAttachments,
      setBusinessId,
      setCheckNumber,
      setCoa,
      setCustomersAndProjects,
      setDate,
      setDateError,
      setExcludeF2S,
      setExcludeReports,
      setFlag,
      setMemo,
      setPayee,
      setStatus,
      setTags,
      updateAmount,
      updateReviewed,
    },
    values: {
      accountId,
      amount,
      attachments,
      businessId,
      checkNumber,
      create,
      customersAndProjects,
      isBill,
      isReviewed,
      memo,
      payee,
      status,
      userFlag,
    },
  } = viewModel;
  const { classes, cx } = useStyles();
  const txnHeaderProps = useMemo<React.ComponentProps<typeof TxnHeaderSection>>(
    () => ({
      cpSection: <CardPaymentStatementNote splitSeriesFf={splitSeriesFf} txn={txn} />,
      txn,
      model,
      create,
      status,
      isBill,
      unMarkAsBill,
      markRecurring,
      setFlag,
      flag: userFlag,
      disabledRecurring: !completeEnough || isAccountClosed || fullDisableQBP,
      viewRecurring: onViewRecurring,
      setEditSeries: onSetEditSeries,
      updateReviewed,
      reviewed: isReviewed,
      setStatus,
      unlinkRecurring,
      disabled: fullDisableQBP,
    }),
    [
      completeEnough,
      create,
      fullDisableQBP,
      isAccountClosed,
      isBill,
      isReviewed,
      markRecurring,
      model,
      onSetEditSeries,
      onViewRecurring,
      setFlag,
      setStatus,
      splitSeriesFf,
      status,
      txn,
      unlinkRecurring,
      unMarkAsBill,
      updateReviewed,
      userFlag,
    ],
  );
  const amountAccountProps = useMemo<React.ComponentProps<typeof AmountAccountRow>>(
    () => ({
      accountId,
      allAccounts,
      amount,
      fullDisableQBP,
      goToOtherSide,
      hasSplitSeries,
      hasSplitTransferCounterpart,
      isAssetPayment,
      isIncome,
      isManual,
      isMatched,
      linkedExpense: Boolean(linkedExpense),
      openAccounts,
      openSplitDialog,
      selectAccount,
      showMatchOrTransferRow: Boolean(showMatchOrTransferRow),
      showPaymentDialogLink,
      split,
      supportedTransfer,
      txn,
      unMatchTransaction,
      updateAmount,
    }),
    [
      accountId,
      allAccounts,
      amount,
      fullDisableQBP,
      goToOtherSide,
      hasSplitSeries,
      hasSplitTransferCounterpart,
      isAssetPayment,
      isIncome,
      isManual,
      isMatched,
      linkedExpense,
      openAccounts,
      openSplitDialog,
      selectAccount,
      showMatchOrTransferRow,
      showPaymentDialogLink,
      split,
      supportedTransfer,
      txn,
      unMatchTransaction,
      updateAmount,
    ],
  );
  const categorySectionProps = useMemo<React.ComponentProps<typeof CategorySection>>(
    () => ({
      accountId,
      coa,
      setCoa,
      setTags,
      amount,
      businessId,
      setBusinessId,
      customersAndProjects,
      resetCustomersAndProjects,
      validPayee,
      onOpenSplitDialog: openSplitDialog,
      txn: currentTxn,
      showSplitRow: !receiptId && refundTracker && !hasSplitTransferCounterpart,
      splitsDisabled: splitsDisabled || !!disableSalesReceiptFields || hasSplitTransferCounterpart,
      disabled: Boolean(
        showPaymentDialogLink || linkedExpense || disableSalesReceiptFields || hasSplitTransferCounterpart,
      ),
      tagsDisabled: Boolean(showPaymentDialogLink || linkedExpense || disableSalesReceiptFields),
      goToOtherSide,
      disabledPayment: disabledPayment(businessId),
    }),
    [
      accountId,
      amount,
      businessId,
      coa,
      currentTxn,
      customersAndProjects,
      disableSalesReceiptFields,
      disabledPayment,
      goToOtherSide,
      hasSplitTransferCounterpart,
      linkedExpense,
      openSplitDialog,
      receiptId,
      refundTracker,
      resetCustomersAndProjects,
      setBusinessId,
      setCoa,
      setTags,
      showPaymentDialogLink,
      splitsDisabled,
      validPayee,
    ],
  );
  const bottomSectionProps = useMemo<React.ComponentProps<typeof BottomSection>>(
    () => ({
      paymentId,
      memo,
      setMemo,
      attachments,
      setAttachments,
      showCheckNumberField,
      checkNumber,
      setCheckNumber,
      disableReportsValue,
      visibilityF2SDisplay,
      visibilityExcludeReportsDisplay,
      setExcludeF2S,
      setExcludeReports,
      hasSplitItemsDefined,
      hasSplitTransferCounterpart,
      openSplitDialogFromExclusionsBanner,
      editSplitsFromExcludeBannerDisabled,
    }),
    [
      attachments,
      checkNumber,
      disableReportsValue,
      editSplitsFromExcludeBannerDisabled,
      hasSplitItemsDefined,
      hasSplitTransferCounterpart,
      memo,
      openSplitDialogFromExclusionsBanner,
      paymentId,
      setAttachments,
      setCheckNumber,
      setExcludeF2S,
      setExcludeReports,
      setMemo,
      showCheckNumberField,
      visibilityExcludeReportsDisplay,
      visibilityF2SDisplay,
    ],
  );

  if (isTxnFromArAccount) {
    return (
      <ZeroStateView
        button={
          <Button id='try-again-button' variant='outlined' onClick={() => closeAll()}>
            Go Back
          </Button>
        }
        id='failed-to-fetch-accounts'
        title='Oops'
        description='Looks like you tried to access a read-only transaction.'
        qIllustration='accounts'
      />
    );
  }

  return (
    <>
      <Dump obj={{ isDirty, completeEnough, currentTxn, txn }} />
      <StaleTransactionRefreshBanner in={isDirtyOutside} onRefreshClick={onRefreshClick} />

      {showRecurringOptionsInline ? (
        <RecurringOptions
          isTransfer={isTransfer}
          isIncome={isIncome}
          createRecurring={createRecurring}
          startRecurringSelect={startRecurringSelect}
          doOneTimeBill={doOneTimeBill}
        />
      ) : rootModalDrawer && enhancedDetailView ? (
        <StdDialogContent id='transaction-details-content' className={classes.drawerContent} sx={{ minHeight: 0 }}>
          <TransactionEditDrawerBody viewModel={viewModel} rootModalDrawer={rootModalDrawer} />
        </StdDialogContent>
      ) : (
        <StdDialogContent id='transaction-details-content' className={classes.content} sx={{ minHeight: 0 }}>
          <TxnHeaderSection {...txnHeaderProps} />

          <div className={classes.dividerSection}>
            <div className={classes.divider} />
          </div>

          <PayeeDateRow
            date={date}
            dateError={dateError}
            dateInfoText={dateInfoText}
            disabled={fullDisableQBP || hasSplitTransferCounterpart}
            focusSplits={focusSplits}
            futureTxnsFf={Boolean(futureTxnsFf)}
            payee={payee}
            setDate={setDate}
            setDateError={setDateError}
            setPayee={setPayee}
          />
          <AmountAccountRow {...amountAccountProps} />

          {refundTracker && !hasSplitTransferCounterpart && (
            <div className={cx(classes.refundLine, classes.refundAfterAmountRow)} id='refund-line'>
              <RefundLine
                txn={currentTxn}
                disabled={Boolean(showPaymentDialogLink || linkedExpense || disableSalesReceiptFields)}
                rootModalDrawer={rootModalDrawer}
              />
            </div>
          )}

          {receiptId ? (
            <ReceiptSection receiptId={receiptId} hasSplits={hasSplitItemsDefined} onCreateSplits={openReceiptSplits} />
          ) : null}

          <CategorySection {...categorySectionProps} />

          {businessEnabled && !hideClientsAndProjects ? (
            <ClientProjectSelect
              value={customersAndProjects}
              onChange={setCustomersAndProjects}
              className={classes.clientProjectSelect}
              ornamentType={ornamentType}
              disabledInvoicePayment={disabledPayment(businessId) || disableSalesReceiptFields}
              disabledSelection={
                showPaymentDialogLink ||
                linkedExpense ||
                currentSplitPayments > 0 ||
                hasSplitTransferCounterpart ||
                disableSalesReceiptFields
              }
              invoiceId={currentTxn?.linkedItem?.id}
              paymentAssetId={currentTxn?.id}
              onButtonClick={closeAll}
            />
          ) : null}

          <div className={classes.dividerSection}>
            <div className={classes.divider} />
          </div>

          <BottomSection {...bottomSectionProps} />
        </StdDialogContent>
      )}

      {!showRecurringOptionsInline && (
        <DialogActionsFooter
          create={create}
          currentTxn={currentTxn}
          deleteQTip={deleteQTip}
          fullDisableQBP={fullDisableQBP}
          hideCreateRule={hideCreateRule}
          hideDelete={Boolean(rootModalDrawer) && enhancedDetailView}
          completeEnough={completeEnough}
          isDirty={isDirty}
          onDelete={onDelete}
          onCreateRule={onCreateRule}
          onSave={onSave}
        />
      )}
    </>
  );
}
